<template>
  <div>
    <v-row>
      <v-col class="text-h5 gray--text text--lighten-1">
        Informações da lente
      </v-col>

      <v-col cols="auto">
        <v-switch
          :value="isAdapted"
          @change="changeIsAdapted"
          dense
          class="v-input--reverse font-weight-bold"
          :disabled="isFormDisabled"
        >
          <template #label>
            <span class="text-h5 gray--text"> Adaptada </span>
          </template>
        </v-switch>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="auto">
        <v-radio-group
          :value="eye"
          @change="changeEye"
          row
          :disabled="isFormDisabled"
        >
          <v-radio label="Olho direito" value="Olho direito" />
          <v-radio label="Olho esquerdo" value="Olho esquerdo" />
        </v-radio-group>
      </v-col>

      <v-col>
        <v-combobox
          :value="contactLensTestGroup"
          @input="$emit('update:contactLensTestGroup', $event)"
          label="Caixa"
          dense
          outlined
          clearable
          item-text="name"
          background-color="white"
          :items="orderedContactLensTestGroups"
          :hide-details="!errors.contactLensTestGroup"
          :error-messages="errors.contactLensTestGroup"
          :disabled="isFormDisabled"
          @change="changeGroup"
        />
      </v-col>

      <v-col>
        <v-combobox
          :value="material"
          @input="$emit('update:material', $event)"
          label="Material"
          dense
          hide-details
          outlined
          item-text="name"
          background-color="white"
          :items="materials"
          :disabled="hasContactLensTestGroup || isFormDisabled"
          @change="dispatchAutoSave"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <v-combobox
          :value="contactLensTestLens"
          @input="$emit('update:contactLensTestLens', $event)"
          label="Lente"
          dense
          outlined
          clearable
          item-text="name"
          background-color="white"
          :items="contactLenses"
          :loading="contactLensesLoading"
          :hide-details="!errors.contactLensTestLens"
          :error-messages="errors.contactLensTestLens"
          :disabled="contactLensesLoading || isFormDisabled"
          @change="changeLens"
        />
      </v-col>

      <v-col>
        <v-text-field
          :value="diopter"
          @input="$emit('update:diopter', $event)"
          label="Dioptria"
          dense
          hide-details
          outlined
          background-color="white"
          :disabled="hasContactLensTestLens || isFormDisabled"
          @change="dispatchAutoSave"
        />
      </v-col>

      <v-col>
        <v-text-field
          :value="radius"
          @input="$emit('update:radius', $event)"
          label="Raio"
          dense
          hide-details
          outlined
          background-color="white"
          :disabled="hasContactLensTestLens || isFormDisabled"
          @change="dispatchAutoSave"
        />
      </v-col>

      <v-col>
        <v-combobox
          :value="contactLensSpherical"
          @input="$emit('update:contactLensSpherical', $event)"
          label="Grau"
          dense
          hide-details
          outlined
          background-color="white"
          :items="sphericalFacilitators"
          :menu-props="{ auto: true, maxHeight: 304 }"
          :disabled="hasContactLensTestLens || isFormDisabled"
          @change="changeSpherical"
        />
      </v-col>

      <v-col>
        <v-text-field
          :value="diameter"
          @input="$emit('update:diameter', $event)"
          label="Diâmetro"
          dense
          hide-details
          outlined
          background-color="white"
          :disabled="hasContactLensTestLens || isFormDisabled"
          @change="dispatchAutoSave"
        />
      </v-col>
    </v-row>

    <v-row align="center">
      <v-col>
        <v-textarea
          :value="contactLensObservation"
          @input="changeContactLensObservation"
          label="Observação"
          v-uppercase
          dense
          hide-details
          outlined
          background-color="white"
          :disabled="hasContactLensTestLens || isFormDisabled"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { sortContactLenses } from "@/views/contact_lens_tests/form/sortContactLenses.js";
import { normalizeString } from "@/helpers/string_helpers.js";

export default {
  name: "ContactLensTestsFormLensInformationSection",
  props: {
    facilitators: {
      type: Array,
      required: true,
    },
    materials: {
      type: Array,
      required: true,
    },
    isAdapted: Boolean,
    eye: String,
    contactLensTestGroup: Object,
    material: Object,
    contactLensTestLens: Object,
    diopter: String,
    radius: String,
    contactLensSpherical: String,
    diameter: String,
    contactLensObservation: String,
    isFormDisabled: Boolean,
    errors: Object,
    contactLensTestGroups: Array,
    hasContactLensTestLens: Boolean,
  },
  apollo: {
    contactLenses: {
      query: require("@/graphql/contact_lens_test/lens.gql"),
      update: (data) => sortContactLenses(data.contactLensTestLens),
      variables() {
        return {
          contactLensGroupId: this.contactLensTestGroup.id,
          deleted: false,
        };
      },
      skip() {
        return !this.hasContactLensTestGroup;
      },
    },
  },
  computed: {
    sphericalFacilitators() {
      const facilitator = this.lodash.find(this.facilitators, {
        facilitatorType: "spherical",
      });

      return facilitator?.values || [];
    },
    hasContactLensTestGroup() {
      return Boolean(this.contactLensTestGroup?.id);
    },
    contactLensesLoading() {
      return this.$apollo.queries.contactLenses.loading;
    },
    orderedContactLensTestGroups() {
      return this.lodash.orderBy(
        this.contactLensTestGroups,
        [(e) => normalizeString(e.name)],
        ["asc"]
      );
    },
  },
  methods: {
    changeEye(event) {
      this.$emit("update:eye", event);
      this.dispatchAutoSave();
    },
    async changeGroup() {
      this.contactLenses = [];
      this.$emit("update:contactLensTestLens", null);

      await this.$nextTick();

      if (this.isFormDisabled) return;
      if (!this.contactLensTestGroup?.material?.id) return;

      const material = this.lodash.find(this.materials, {
        id: this.contactLensTestGroup.material.id,
      });

      this.$emit("update:material", material);

      this.dispatchAutoSave();
    },

    async changeLens() {
      await this.$nextTick();

      if (this.isFormDisabled) return;
      if (!this.contactLensTestLens?.id) return;

      this.$emit("update:diopter", this.contactLensTestLens.diopter);
      this.$emit("update:radius", this.contactLensTestLens.radius);
      this.$emit("update:diameter", this.contactLensTestLens.diameter);
      this.$emit(
        "update:contactLensSpherical",
        this.contactLensTestLens.spherical
      );
      this.$emit(
        "update:contactLensObservation",
        this.contactLensTestLens.observation
      );

      this.changeSpherical();
      this.dispatchAutoSave();
    },
    dispatchAutoSave() {
      this.$emit("dispatch-auto-save");
    },
    changeIsAdapted(event) {
      this.$emit("update:isAdapted", event);
      this.dispatchAutoSave();
    },
    changeSpherical() {
      this.$emit("change-spherical");
    },
    changeContactLensObservation(event) {
      this.$emit("update:contactLensObservation", event);
      this.dispatchAutoSave();
    },
  },
};
</script>

<style></style>
